// src/components/WorkTimeReportModal.js
import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "../config";
import "./WorkTimeReportModal.css";

function formatDatePL(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("pl-PL");
}

function formatTimeHM(timeStr) {
  if (!timeStr) return "-";
  return String(timeStr).slice(0, 5); // obsługa "08:00:00" i "08:00"
}

function buildHourOptions() {
  const arr = [];
  for (let h = 0; h <= 24; h++) {
    arr.push(`${String(h).padStart(2, "0")}:00`);
  }
  return arr;
}

function computeHoursFromRange(start, end) {
  if (!start || !end) return null;

  const [sh] = String(start).split(":").map(Number);
  const [eh] = String(end).split(":").map(Number);

  if (!Number.isFinite(sh) || !Number.isFinite(eh)) return null;

  let diff = eh - sh;
  if (diff < 0) diff += 24; // przejście przez północ
  if (diff === 0 && start === "00:00" && end === "00:00") return 24;

  return diff;
}

function isOverrideRow(row) {
  // Pewniejsza detekcja niż porównywanie tylko updated_at
  return (
    row.start_time_override !== null ||
    row.end_time_override !== null ||
    row.hours_override !== null ||
    row.note_override !== null
  );
}

export default function WorkTimeReportModal({ visible, orderId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    totalHours: 0,
    summary: {},
    entries: [],
  });

  // drafty per row
  const [draftHours, setDraftHours] = useState({});   // { [rowId]: "4" }
  const [draftStart, setDraftStart] = useState({});   // { [rowId]: "08:00" }
  const [draftEnd, setDraftEnd] = useState({});       // { [rowId]: "12:00" }

  const hourOptions = buildHourOptions();

  // refs do utrzymania scrolla po zapisie
  const modalRef = useRef(null);
  const tableWrapRef = useRef(null);
  const scrollPosRef = useRef({
    modalScrollTop: 0,
    tableScrollLeft: 0,
  });

  const captureScrollPosition = () => {
    scrollPosRef.current = {
      modalScrollTop: modalRef.current?.scrollTop || 0,
      tableScrollLeft: tableWrapRef.current?.scrollLeft || 0,
    };
  };

  const restoreScrollPosition = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (modalRef.current) {
          modalRef.current.scrollTop = scrollPosRef.current.modalScrollTop || 0;
        }
        if (tableWrapRef.current) {
          tableWrapRef.current.scrollLeft = scrollPosRef.current.tableScrollLeft || 0;
        }
      });
    });
  };

  const initDraftsFromResponse = (json) => {
    const nextDraftHours = {};
    const nextDraftStart = {};
    const nextDraftEnd = {};

    (json.entries || []).forEach((row) => {
      // godziny raportowe
      nextDraftHours[row.id] =
        row.hours_report !== null && row.hours_report !== undefined
          ? String(row.hours_report)
          : "";

      // start/end raportowy (już po COALESCE z backendu)
      nextDraftStart[row.id] = formatTimeHM(row.start_time_report || row.start_time_actual || "");
      nextDraftEnd[row.id] = formatTimeHM(row.end_time_report || row.end_time_actual || "");
    });

    setDraftHours(nextDraftHours);
    setDraftStart(nextDraftStart);
    setDraftEnd(nextDraftEnd);
  };

  const fetchReportWorkTime = async ({ silent = false, restoreScroll = false } = {}) => {
    if (!orderId) return;

    if (restoreScroll) captureScrollPosition();

    if (!silent) setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/orders/${orderId}/worktime-report`, {
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Błąd pobierania czasu pracy raportu.");
      }

      const json = await res.json();
      setData(json);
      initDraftsFromResponse(json);

      if (restoreScroll) restoreScrollPosition();
    } catch (e) {
      console.error(e);
      setError(e.message || "Błąd pobierania danych.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchReportWorkTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, orderId]);

  if (!visible) return null;

  const handleDraftHoursChange = (rowId, value) => {
    setDraftHours((prev) => ({ ...prev, [rowId]: value }));
  };

  const handleDraftStartChange = (rowId, value) => {
    setDraftStart((prev) => ({ ...prev, [rowId]: value }));

    // auto-przeliczenie hours na podstawie start/end (frontend helper)
    const end = draftEnd[rowId];
    const computed = computeHoursFromRange(value, end);
    if (computed !== null) {
      setDraftHours((prev) => ({ ...prev, [rowId]: String(computed) }));
    }
  };

  const handleDraftEndChange = (rowId, value) => {
    setDraftEnd((prev) => ({ ...prev, [rowId]: value }));

    const start = draftStart[rowId];
    const computed = computeHoursFromRange(start, value);
    if (computed !== null) {
      setDraftHours((prev) => ({ ...prev, [rowId]: String(computed) }));
    }
  };

  const handleSaveRow = async (row) => {
    setError("");

    const startVal = String(draftStart[row.id] ?? "").trim();
    const endVal = String(draftEnd[row.id] ?? "").trim();

    if (!startVal || !endVal) {
      alert("Uzupełnij pola „Start raport” i „Koniec raport”.");
      return;
    }

    // Zostawiamy jako string z kropką/przecinkiem dla walidacji i debug (backend i tak liczy z czasu)
    const rawHours = String(draftHours[row.id] ?? "").trim();
    if (!rawHours) {
      alert("Nie udało się wyliczyć godzin raportowych. Sprawdź zakres czasu.");
      return;
    }

    // Lekka walidacja lokalna (backend i tak robi finalną)
    const normalized = rawHours.replace(",", ".");
    const parsed = Number(normalized);

    if (!Number.isFinite(parsed)) {
      alert("Godziny raportowe muszą być liczbą.");
      return;
    }

    if (parsed < 0) {
      alert("Godziny raportowe nie mogą być ujemne.");
      return;
    }

    setSavingId(row.id);

    try {
      const res = await fetch(`${API_URL}/orders/working_time/${row.id}/report-override`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          start_time_override: startVal,
          end_time_override: endVal,
          // hours_override wysyłamy opcjonalnie dla kompatybilności / podglądu;
          // backend i tak przeliczy z czasów (priorytet start/end)
          hours_override: parsed,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Błąd zapisu override.");
      }

      await fetchReportWorkTime({ silent: true, restoreScroll: true });
    } catch (e) {
      console.error(e);
      setError(e.message || "Błąd zapisu.");
    } finally {
      setSavingId(null);
    }
  };

  const handleResetRow = async (row) => {
    setSavingId(row.id);
    setError("");

    try {
      const res = await fetch(`${API_URL}/orders/working_time/${row.id}/report-override`, {
        method: "DELETE",
        credentials: "include",
      });

      // 404 traktujemy łagodnie (override już nie istnieje)
      if (!res.ok && res.status !== 404) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Błąd usuwania override.");
      }

      await fetchReportWorkTime({ silent: true, restoreScroll: true });
    } catch (e) {
      console.error(e);
      setError(e.message || "Błąd usuwania override.");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal wtrm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wtrm-title"
        ref={modalRef}
      >
        <h3 id="wtrm-title" className="wtrm-title">
          Czas Pracy Raportu
        </h3>

        <p className="wtrm-note">
          Edycja dotyczy wyłącznie raportu (PDF/Excel). Stan faktyczny pozostaje bez zmian.
          <br />
        </p>

        {error && <p className="wtrm-error">{error}</p>}

        {loading ? (
          <p className="wtrm-loading">Ładowanie...</p>
        ) : (
          <>
            <div className="wtrm-summary">
              <span className="wtrm-summary-label">Łączny czas do raportu:</span>
              <span className="wtrm-summary-value">{Number(data.totalHours || 0)} h</span>
            </div>

            {!data.entries || data.entries.length === 0 ? (
              <p className="wtrm-empty">Brak wpisów czasu pracy dla tego zlecenia.</p>
            ) : (
              <div className="wtrm-table-wrap" ref={tableWrapRef}>
                <table className="wtrm-table">
                  <thead>
                    <tr>
                      <th>Pracownik</th>
                      <th>Data</th>
                      <th>Zakres faktyczny</th>
                      <th>Faktyczne (working_time)</th>
                      <th>Start raport</th>
                      <th>Koniec raport</th>
                      <th>Raportowe</th>
                      <th>Status</th>
                      <th className="wtrm-col-actions">Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.map((row) => {
                      const hasOverride = isOverrideRow(row);

                      const actualStart = formatTimeHM(row.start_time_actual);
                      const actualEnd = formatTimeHM(row.end_time_actual);
                      const reportStart = draftStart[row.id] ?? formatTimeHM(row.start_time_report);
                      const reportEnd = draftEnd[row.id] ?? formatTimeHM(row.end_time_report);

                      return (
                        <tr key={row.id}>
                          <td className="wtrm-col-worker">
                            {row.worker_first_name} {row.worker_last_name}
                          </td>

                          <td className="wtrm-col-date">{formatDatePL(row.work_date)}</td>

                          <td className="wtrm-col-range">
                            {actualStart} - {actualEnd}
                          </td>

                          <td className="wtrm-col-hours">{Number(row.hours_actual)} h</td>

                          <td className="wtrm-col-select">
                            <select
                              value={reportStart}
                              onChange={(e) => handleDraftStartChange(row.id, e.target.value)}
                              className="modern-edit-select wtrm-time-select"
                              disabled={savingId === row.id}
                            >
                              {hourOptions.map((opt) => (
                                <option key={`start-${row.id}-${opt}`} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="wtrm-col-select">
                            <select
                              value={reportEnd}
                              onChange={(e) => handleDraftEndChange(row.id, e.target.value)}
                              className="modern-edit-select wtrm-time-select"
                              disabled={savingId === row.id}
                            >
                              {hourOptions.map((opt) => (
                                <option key={`end-${row.id}-${opt}`} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td>
                            <input
                              type="text"
                              value={draftHours[row.id] ?? ""}
                              onChange={(e) => handleDraftHoursChange(row.id, e.target.value)}
                              className="modern-edit-input wtrm-hours-input"
                              placeholder="np. 4"
                              disabled={savingId === row.id}
                              title="Wartość wyliczana z zakresu czasu (możesz też wpisać ręcznie)"
                            />
                          </td>

                          <td>
                            {hasOverride ? (
                              <span className="wtrm-badge wtrm-badge--changed">Nadpisane</span>
                            ) : (
                              <span className="wtrm-badge wtrm-badge--default">Oryginał</span>
                            )}
                          </td>

                          <td className="wtrm-col-actions">
                            <div className="wtrm-actions">
                              <button
                                className="btn-submit"
                                onClick={() => handleSaveRow(row)}
                                disabled={savingId === row.id}
                              >
                                {savingId === row.id ? "Zapisywanie..." : "Zapisz"}
                              </button>

                              <button
                                className="btn-cancel"
                                onClick={() => handleResetRow(row)}
                                disabled={savingId === row.id}
                              >
                                Przywróć oryginał
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="modal-buttons wtrm-footer">
              <button className="btn-submit" onClick={() => fetchReportWorkTime()}>
                Odśwież
              </button>
              <button className="btn-cancel" onClick={onClose}>
                Zamknij
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}